import { ChangeDetectorRef, Component, ElementRef, Inject, Injector } from "@angular/core";
import { forkJoin, from, Observable, of } from "rxjs";
import { catchError, first } from "rxjs/operators";
import { DestroyBase } from "../../../components/common/ng-destroy-base";
import { ReportDesignerPageComponent } from "../components/report-designer-page/report-designer-page.component";
import { WidgetBoxComponent } from "../components/widget-box/widget-box.component";
import { HashtagService } from "../hashtags/services/hashtag.service";
import { ColorPalette } from "./color-palette";
import { WidgetService } from "./widget.service";
import { EventBusService } from "../../../services/event-bus.service";
import { ReportDesignerTranslatorService } from "../services/report-designer-translator.service";
import { ReportDesignerStorageService } from "../services/report-designer-storage.service";
import { ObjectUtil } from "../../../components/common/object-util/object-util";
import { ReportDesignerWidgetConfig } from "../models/report-designer-widget-config";
import { WidgetLockerService } from "../services/WidgetLockerService";
import { ReportDesignerWidgetSettings } from "../models/report-designer-widget-settings";
import { HashService } from "../../project-valuation/services/hash.service";

/**
 * Base class for widgets
 */
@Component({ template: '' })
export abstract class WidgetBaseComponent extends DestroyBase {
  public widgetBox: WidgetBoxComponent;

  /**
   * ReportDesignerTranslatorService
   */
  protected reportDesignerTranslator: ReportDesignerTranslatorService;

  /**
   * EventBusService
   */
  protected eventBus: EventBusService

  /**
   * WidgetService
   */
  protected widgetService: WidgetService;

  /**
   * ChangeDetectorRef
   */
  public ref: ChangeDetectorRef;//Used externally

  /**
   * HashtagService
   */
  protected hashtagService: HashtagService;

  /**
   * ReportDesignerStorageService
   */
  protected rdStorage: ReportDesignerStorageService;

  /**
   * WidgetLockerService
   */
  protected widgetLocker: WidgetLockerService;

  /**
   * HashService
   */
  protected hashService: HashService;

  /**
   * Constructor
   * @param injector Injector
   * @param name The widget name
   * @param isStatic Is this a static widget? Use in combination with OnPush CD strategy to disable CD and gain performance. All CD has to be triggered by code!!!
   */
  constructor(protected injector: Injector, @Inject(String) public name: string, @Inject(Boolean) protected isStatic: boolean) {
    super();
    this.eventBus = this.injector.get(EventBusService);
    this.hashtagService = this.injector.get(HashtagService);
    this.widgetService = this.injector.get(WidgetService);
    this.ref = this.injector.get(ChangeDetectorRef);
    this.rdStorage = this.injector.get(ReportDesignerStorageService);
    this.reportDesignerTranslator = this.injector.get(ReportDesignerTranslatorService);
    this.widgetLocker = this.injector.get(WidgetLockerService);
    this.hashService = this.injector.get(HashService);
    this.translationName = name;

    //disable chart transitions (animations) in print mode
    this.chartTransitionsEnabled = !this.rdStorage.getAppPrintMode();
  }

  /**
   * The current layout
   */
  public currentLayout = 'default';

  /**
   * The list of available layouts
   */
  public availableLayouts = [];

  /**
 * The current calculation
 */
  public currentCalculation = 1;

  /**
   * The list of available calculations
   */
  public availableCalculations = [];

  /**
   * The widget's unique id. Set by the widget box
   */
  public widgetGuid: string;

  /**
   * The widget's data observable
   */
  protected widgetData$: Observable<any>;

  /**
   * The widget's mockup data observable
   */
  protected mockupData$: Observable<any>;

  /**
   * The widget's template data observable
   */
  protected templateData$: Observable<any>;

  /**
   * The widget's data observable
   */
  protected reflists$: { name: string, includeZeroEntryIfMissing: boolean }[] = [];

  /**
   * The widget's data object
   */
  private _widgetData: any;

  /**
   * The initial widget data
   */
  private _initialWidgetData: any;

  /**
   * The last width. Used for size change detection
   */
  private _lastWidth: number;

  /**
   * The last height. Used for size change detection
   */
  private _lastHeight: number;

  /**
   * Map that holds info if text&list section has fields available for adding
   * Used to f.e. hide Add button if no more fields to add
   */
  private _textAndListSectionHasFieldsAvailableMap: Map<string, boolean> = new Map<string, boolean>();

  /**
   * Text scale for Donut charts
   */
  protected textScale = 1;

  /**
  * Donut hole scale for Donut charts
  */
  protected donutHoleScale = 1;

  /**
  * Is widget read only
  */
  protected isWidgetReadOnly = false;

  /** Selected hashtag */
  public hashTag: string;

  /**
* TranslationName used for translation (jsons), f.e. keyFigures etc..
* Should be aligned with widget Dataverse DTO name (without Widget prefix and camel case)
*/
  public translationName: string;

  /**
   * Sets the initial widget data
   * @param data the initial widget data
   */
  private setInitialData(data) {
    this._initialWidgetData = (data ? JSON.stringify(data) : '').split('T00:00:00Z').join('');
  }

  /**
   * Indicates if the widget data was changed
   */
  private get widgetDataChanged() {
    //widget data should always be JS object, so safe to stringify
    //additionally adjust dates as they can be represented with or without hour:min part
    return this.isWidgetReadOnly ? false : JSON.stringify(this._widgetData).split('T00:00:00Z').join('') !== this._initialWidgetData;
  }

  /**
   * Getter for the widget data
   */
  public get widgetData() {
    return this._widgetData;
  }

  /**
   * setter for the widget data
   * @param data the initial widget data
   */
  protected set widgetData(data: any) {
    this._widgetData = data;
    this._widgetDataHash = this.hashService.generate(JSON.stringify(data));
    if (!this.isWidgetReadOnly) {
      this.setInitialData(data);
    }
  }

  private _widgetDataHash: string = null;

  public get widgetDataHash() {
    return this._widgetDataHash;
  }

  /**
   * Updates widget data
   * @param data {any} New widget data
   */
  protected updateWidgetData(data: any) {
    this._widgetData = data;
  }

  /**
   * The widget's mockup data object
   */
  protected mockupData: any;

  /**
   * The widget's template data object
   */
  protected templateData: any;

  /**
   * The ref lists
   */
  protected reflists: any = {};

  /**
   * Should widget display table data
   */
  protected displayTableData = false;

  /**
   * The widget's configuration. Set by the widget box
   */
  public widgetConfig: ReportDesignerWidgetConfig;

  /**
   * The page component where this widget is located. Set by the widget box
   */
  public page: ReportDesignerPageComponent = null;

  /**
  * Should we use mockup data
  */
  public mockup = false;

  /**
  * Should we force loading mockup data (f.e. for TextWidget that use mockup data in QuickView/Print)
  */
  public forceLoadingMockupData = false;

  /**
  * Template oid
  */
  public templateId = 0;

  /**
  * Dirty flag
  */
  protected isDirty = false;

  /**
   * Selected currency on proj level
   */
  public currency: string;

  /**
   * Selected unit of length on proj level
   */
  public unitOfLength: string;

  /**
   * unit of area helper (unitOfLength²)
   */
  public unitOfArea: string;

  /**
   * rdTranslate replacements (default for currency, unitOfLength and unitOfArea)
   */
  public translateReplacements: Map<string, string> = new Map<string, string>();

  /**
  * Flag if widgetDataLoaded logic should be skipped in base class and be executed in master widget class, after all other addiitonal data is loaded
  */
  public executeWidgetDataLoadedInWidgetClass = false;

  public skipWidgetDataLoadedCall = false;

  /**
   * Is this Master widget (always in "normal" scenario if not slave page widget in virtual pages scenario)
   */
  protected isMasterWidget = false;

  /**
   * Widget edit mode
   */
  public editMode = false;

  /**
   * Should chart transitions be enabled (should be disabled in print mode)
   */
  public chartTransitionsEnabled = true;

  /**
   * Register events callback
   */
  abstract registerEvents(): void;

  /**
   * Initialized callback
   */
  abstract onInitialized(): void;

  /**
   * Initializes the widget by loading the widget's configuration, subscribing to RD events and trigering loadData(). Can be overridden. 
   */
  protected initWidgetConfig(): void {
    this.widgetService.localizationService.setTranslationOverride(null);//Translation override is reset every time a widget is loaded. This way, we can make sure it's reset every time a widget is loaded in RD in the same session as it was opened in dictionary
    if (this.widgetGuid) { //report designer
      this.widgetService.getWidgetInfo(this.widgetGuid).then((widgetInfo) => {
        this.widgetConfig = widgetInfo.widgetConfig;
        this.page = widgetInfo.page;
        this.initialization();
      }).catch(() => {
        //ignore
      });

    }
    else { // dictionary
      this.page = null;
      this.initialization();
    }
  }

  /**
 * initialization
 */
  private initialization(): void {
    const setting = this.widgetConfig?.widgetSettings;
    this.currentLayout = setting?.currentLayout || 'default';
    this.setupCurrentCalculation(setting);

    this.setupCurrentLayout(setting);


    this.setupTemplateId();

    this.mockup = this.mockup || !this.getReport();

    this.currency = this.rdStorage.getCurrencyDescription();
    this.unitOfLength = this.rdStorage.getUnitofLength();
    this.unitOfArea = `${this.unitOfLength}²`;

    //this values can be used in rdTranslate pipe (calling pipe like rdTranslate:translateReplacements) to display currency/units if required
    this.translateReplacements.set('{{currency}}', this.currency);
    this.translateReplacements.set('{{unitOfLength}}', this.unitOfLength);
    this.translateReplacements.set('{{unitOfArea}}', this.unitOfArea);

    this.isMasterWidget = typeof (this.widgetConfig?.widgetSettings.slavePageNo) === "undefined";

    this.registerEvents();
    this.onInitialized();

    //if widgets support hashtags, subscribe to selectedHashtag emitter
    this.prepareHashtagSelectionHandler();

    if (!this.reflists$) {
      this.reflists$ = [];
    }

    if (this.widgetData$) {
      this.loadDataInternal();
    }

    this.subscribeRequiredEvents();
  }

  /**
   * Set the current layout
   * @param setting The widget settings
   */
  private setupCurrentLayout(setting: ReportDesignerWidgetSettings) {
    if (this.availableLayouts?.length === 0) {
      // if not already set by widget itself (f.e. picture widget or text widget) => set to configured layouts
      this.availableLayouts = setting?.availableLayouts || [];
    }

    if (this.availableLayouts?.length && this.currentLayout !== 'ChosenOnAdd') {
      // if layout does not exists, set it to first available
      if (this.availableLayouts.indexOf(this.currentLayout) === -1) {
        this.currentLayout = this.availableLayouts[0];
        this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;
      }
    }
  }

  /**
   * Setup the templateId
   */
  private setupTemplateId() {
    const templateObj = this.rdStorage.getTemplate();
    if (templateObj) {
      this.templateId = templateObj.Oid;
    }
    else {
      this.templateId = null;
    }
  }

  /**
   * Setup the current calculation
   * @param setting
   */
  private setupCurrentCalculation(setting: ReportDesignerWidgetSettings) {
    if (setting?.currentCalculation) {
      this.currentCalculation = setting?.currentCalculation;
    }
    this.currentCalculation = setting?.currentCalculation || 1;


    if (this.availableCalculations?.length === 0) {
      // if not already set by widget itself => set to configured calculations
      this.availableCalculations = setting?.availableCalculations || [];
    }

    if (this.availableCalculations?.length) {
      // if calculation does not exists, set it to first available
      if (!this.availableCalculations.filter((calc) => { return this.currentCalculation == calc.id; }).length) {
        this.currentCalculation = this.availableCalculations[0].id;
      }
    }
  }

  /**
   * Prepare the hashtag selection handler
   */
  private prepareHashtagSelectionHandler(): void {
    if (this.widgetConfig?.hashtagSupported) {
      this.hashtagService.selectedHashtag$.subscribe(tag => {
        //if acive wiget is same as current widget add selected hashtag
        if (tag && tag.widgetId === this.widgetConfig.widgetId) {
          this.hashTag = tag.hashtag.key;
        }
      });

      this.hashtagService.getAllHashtags();
    }
  }

  /**
   * Subscribe to all required events (event bus)
   */
  private subscribeRequiredEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('widgetResized', (widget) => {
      if (this.widgetConfig.widgetSettings.id === widget.widgetSettings.id) {
        this.widgetResized();
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('activateWidget', (widgetBoxHeader: WidgetBoxComponent) => {
      if (this.widgetConfig.widgetSettings.id == widgetBoxHeader.widgetId) {
        widgetBoxHeader.updateModifiedInfo(this.widgetData);
        this.page.document.main.setActiveWidget(this);
      }
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportDesignerLocaleChanged', (localeScriptDesignator) => {
      setTimeout(() => {
        if (this.isStatic && this.ref !== null) {
          this.ref.markForCheck();
        }

        this.onReportDesignerLocaleChanged(localeScriptDesignator);
      });
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('reportDesigner.editModeChanged', () => {
      setTimeout(() => {
        this.checkEditMode();
        if (this.isStatic && this.ref !== null) {
          this.ref.markForCheck();
        }
      });
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('report.designer.widget.locks.changed', () => {
      this.checkEditMode();
    }));
  }

  /**
   * Checks edit mode
   */
  private checkEditMode(): void {
    const lockInfo = this.widgetLocker.getLockInfoForWidget(this.widgetConfig);
    const priorEditMode = this.editMode;
    this.editMode = this.page.editMode && lockInfo?.lockedByCurrentUser;
    if (this.isStatic && this.ref !== null) {
      this.ref.markForCheck();
    }

    if (this.editMode !== priorEditMode) {
      this.editModeChanged(this.editMode);
    }
  }

  /**
   * Widget edit mode changed
   * @param newEditMode New edit mode
   */
  protected editModeChanged(newEditMode: boolean) {
    //default does nothing
  }

  /**
   * Loads a reflist
   * @param name The reflist name
   */
  protected loadReflist(name: string, includeZeroEntryIfMissing = false): void {
    this.reflists$.push({ name: name, includeZeroEntryIfMissing });
  }

  /**
   * Reload the data
   */
  protected reloadData(): void {
    this.loadDataInternal();
  }

  /**
   * Load data
   */
  private loadDataInternal(): void {
    //if obervable is set in onInitialized (and it should)
    //register for change and fire widgetDataLoaded (otherwise widget itself is responsible to do this)
    let templateObservable = of(null);
    if (this.templateData$) {
      templateObservable = this.templateData$;
    }

    const reflistObservable$ = this.buildReflistObservable();

    const shouldLoadMockup = this.mockup || this.forceLoadingMockupData;

    forkJoin([
      this.widgetData$.pipe(first()).pipe(catchError(() => of(null))),
      shouldLoadMockup ? this.mockupData$.pipe(first()).pipe(catchError(() => of(null))) : of({}),
      templateObservable.pipe(first()).pipe(catchError(() => of(null))),
      reflistObservable$.pipe(first()).pipe(catchError(() => of(null))),
      from(this.preloadSvgIcons())
    ]).subscribe((result) => {
      this.onDataLoaded(result);
    });
  }

  /**
   * Widget data ready callback
   */
  protected widgetDataReady(): void {
    //Can be overridden as needed
  }

  /**
   * Data is loaded
   * @param result
   */
  private onDataLoaded(result: [any, any, null, any, any]) {
    this.mockupData = result[1];
    this.templateData = result[2];
    this.processReflistResult(result[3]);

    if (this.mockupData == null) {
      alert("ERROR: Mockup data not loaded!");
    }

    this.widgetData = this.preprocessLoadedData(result[0]);
    this.widgetDataReady();
    if (this.isStatic) {
      this.ref.markForCheck();
    }

    if (!this.skipWidgetDataLoadedCall && !this.executeWidgetDataLoadedInWidgetClass && this.widgetGuid && !this.widgetConfig.hashtagSupported) {
      this.page?.widgetDataLoaded(this.widgetConfig?.widgetSettings.id);
    }

    if (this.widgetConfig.hashtagSupported) {
      /*
        TODO: some "hashtag widgets" will not fire widgetDataLoaded if f.e. hashtagsetvalue is nowhere in HTML
        f.e. InvestmentComparablesWidgetComponent or LeasingComparablesWidgetComponent (hashtagsetvalue directives are in ngFor and not displayed if no data rows)
        probably same for text&list if there are no topics..
        for now quick "timeout fix" but should be handled better
      */
      setTimeout(() => {
        if (!this.skipWidgetDataLoadedCall && !this.widgetConfig.dataLoaded) {
          this.page?.widgetDataLoaded(this.widgetConfig?.widgetSettings.id);
        }
      }, 300);
    }

    //Run in the next VM cycle
    setTimeout(() => {
      if (this.isDirty && !this.widgetDataChanged) {
        this.isDirty = false;
      }

      if (this.widgetConfig.hashtagSupported) {
        this.eventBus.broadcastEvent('loadHashtags', this.widgetConfig?.widgetSettings.id);
      }
    });
  }

  /**
   * Process the reflists results
   * @param reflistsDataArray
   */
  private processReflistResult(reflistsDataArray: any[]): void {
    for (const ref of this.reflists$) {
      let reflistsData = reflistsDataArray[ref.name];
      if (ref.includeZeroEntryIfMissing) {
        reflistsData = this.includeZeroEntryIfMissing(reflistsData);
      }

      if (ref.name === 'ref_l2_projectstd_valuationmethod') {
        reflistsData = reflistsData.filter(data => data.oid !== 0); //do not show empty item
      }

      this.reflists[ref.name] = reflistsData;
    }
  }

  /**
   * Builds an observable for loading the reflists
   * @returns
   */
  private buildReflistObservable(): Observable<any> {
    let reflistObservable$: Observable<any>;
    if (this.reflists$.length) {
      const reflistNames = this.reflists$.map((reflist) => reflist.name);

      reflistObservable$ = this.widgetService.loadReflists(reflistNames).pipe(first());
    }
    else {
      reflistObservable$ = of(null);
    }
    return reflistObservable$;
  }

  /**
  * If you are using svg-icon in your widgets, these will have to be loaded, which happens independent of the RD loading logic.
  * Especially at print, this can lead to race conditions where the widget data is loaded, but the svg icons aren't.
  * Therefore, they can be pre-loaded in this method, which should be overridden in the widget class
  * @returns
  */
  protected preloadSvgIcons(): Promise<any> {
    return Promise.resolve();
  }

  /**
   * Pre-process the loaded data.Override if needed
   * @param data The loaded data
   */
  protected preprocessLoadedData(data): any {
    //default just return same data
    return data;
  }

  /**
   * If the widget is read-only, use this to report successful saving to the RD
   */
  protected useReadOnlySaveChangesHandler(): void {
    this.isWidgetReadOnly = true;
    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', (_payload, _options) => {
      this.saveChangesHandled();
    }));
  }

  /**
   * If the widget can save, use this to save data and report successful saving to the RD
   * @param widgetUrl Widget URL for saving
   * @param provideDataFnAsync
   * @param saveChangesHandlerOnError 
   */
  protected useDefaultServiceSaveChangesHandler(widgetUrl: string, provideDataFnAsync: () => Promise<string>, saveChangesHandlerOnError = true): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveChanges', (_payload, _options) => {
      if (!this.isDirty) {
        this.saveChangesHandled();
      } else {
        provideDataFnAsync().then(data => {
          this.widgetService.saveWidget(widgetUrl, data).then((gpr: any) => {
            if (gpr.success) {
              //reset dirty flag
              this.isDirty = false;
              //set new (saved) initial data
              this.setInitialData(this.widgetData);
              //run success events
              this.saveChangesHandled();
              this.saveCompleted(true);
            } else {
              this.handleSaveError(saveChangesHandlerOnError, null);
            }
          }).catch(err => {
            this.handleSaveError(saveChangesHandlerOnError, err);
          });
        }).catch(err => {
          this.handleSaveError(saveChangesHandlerOnError, err);
        });
      }
    }));
  }


  /**
   * If the widget can save, use this to save data and report successful saving to the RD
   * @param widgetUrl Widget URL for saving
   * @param provideDataFnAsync
   * @param saveChangesHandlerOnError 
   */
  protected useTemplateServiceSaveChangesHandler(widgetUrl: string, provideDataFnAsync: (oid: number) => Promise<string>, saveChangesHandlerOnError = true): void {
    this.onEvents.push(this.eventBus.subscribeEvent('reportDesignerDialog.templateSaved', (params, _options) => {
      const templateData = params.templateData;
      if (!this.isDirty && !templateData.savedAsNew) {
        this.saveChangesHandled();
      } else {
        provideDataFnAsync(templateData.oid).then(data => {
          this.widgetService.saveWidgetTemplate(widgetUrl, data).then((gpr: any) => {
            if (gpr.success) {
              //reset dirty flag
              this.isDirty = false;
              //set new (saved) initial data
              this.setInitialData(this.widgetData);
              //run success events
              this.saveChangesHandled();
              this.saveCompleted(true);
            } else {
              this.handleSaveError(saveChangesHandlerOnError, null);
            }
          }).catch(err => {
            this.handleSaveError(saveChangesHandlerOnError, err);
          });
        }).catch(err => {
          this.handleSaveError(saveChangesHandlerOnError, err);
        });
      }
    }));
  }

  /**
   * Handle a save error
   * @param saveChangesHandlerOnError
   * @param error
   */
  private handleSaveError(saveChangesHandlerOnError: boolean, error: any): void {
    if (saveChangesHandlerOnError) {
      this.saveChangesHandled();
    }

    this.saveCompleted(false, error);
  }

  /**
   * Save callback
   * @param _success
   * @param _err
   */
  protected saveCompleted(_success: boolean, _err: any = null): void {
    //default does nothing
  }

  /**
   * Save changes handler: Inform RD
   */
  protected saveChangesHandled(): void {
    setTimeout(() => {
      this.page.widgetDataSaved(this.widgetConfig.widgetSettings.id);
    });
  }

  /**
 * Mark widget dirty with no checks
 */
  protected forceDirty(): void {
    this.isDirty = true;
  }

  /**
 * Mark widget dirty/not dirty with checks if widget data is really dirty (and widget checked out etc)
 * Required because ngModelChange fires on template init too, so we need to check if data is really dirty
 * @param dirty
 * @param force
 */
  protected setDirtyFlag(dirty: boolean, force: boolean = null): void {
    if (dirty) {
      //if not already dirty and in edit mode and checked out by current user => mark dirty
      const shouldSetDirty = force || (!this.isDirty
        && !this.isWidgetReadOnly
        && this.page.editMode
        && this.widgetConfig.widgetLockInfo && this.widgetConfig.widgetLockInfo.lockedByCurrentUser
        && this.widgetDataChanged);

      if (shouldSetDirty) {
        console.log('setDirtyFlag called!');
        this.isDirty = true;
      }
    } else {
      this.isDirty = false;
    }

    if (this.isStatic) {
      this.ref.markForCheck();
    }
  }

  /**
   * Render callback for Kendo Donut Charts
   * @param e
   */
  protected onRender(e): void {
    const surface = e.sender.surfaceElement;
    const width = surface.nativeElement.scrollWidth;
    const height = surface.nativeElement.scrollHeight;

    if (this._lastWidth !== width || this._lastHeight !== height) {
      //Initial or after resize
      this._lastWidth = width;
      this._lastHeight = height;
      if (surface) {
        const transformedElement = surface.nativeElement.querySelector("g[transform]");
        if (transformedElement) {
          const matrix = transformedElement.getCTM();

          const scaleX = Math.sqrt(matrix.a * matrix.a + matrix.b * matrix.b);
          const scaleY = Math.sqrt(matrix.c * matrix.c + matrix.d * matrix.d);

          this.textScale = Math.min(scaleX, scaleY);
          this.donutHoleScale = Math.min(scaleX, scaleY);

          if (this.textScale === 0) {
            this.textScale = 1;
            this.donutHoleScale = 1;
          }
          return;
        }
      }
      this.textScale = 1;
    }
  }

  /**
   * Indicates if this widget should show table or chart data - if supported
   */
  protected isToggled(): boolean {
    return this.displayTableData;
  }

  /**
   * Toggles if this widget should show table or chart data - if supported
   */
  protected toggle(): void {
    this.displayTableData = !this.displayTableData;
  }

  /**
   * Resize event handler
   */
  protected widgetResized(): void {
    this.widgetConfig.widgetSettings.resizeDataHash = null; //Widget was resized manually
  }

  /**
   * Layout change event handler
   * @param newLayout The new layout
   */
  public layoutChanged(newLayout: string): void {
    if (this.isStatic && this.ref != null) {
      this.ref.markForCheck();
    }
    this.widgetConfig.widgetSettings.currentLayout = newLayout;
  }

  public check() {
    this.ref.markForCheck();
  }

  /**
   * Calculation change event handler
   * @param _newCalculation The new calculation option
   */
  public calculationChanged(_newCalculation: number): void {
    //base class do nothing
  }

  /**
   * Report localization changed
   * @param _localeScriptDesignator locale code, f.e. lang-0001
   */
  onReportDesignerLocaleChanged(_localeScriptDesignator): void {
    //base class do nothing
  }

  /**
   * Gets the widget settings from the report JSON
   * @param attributeName Attribute name
   * @param defaultValue Default value if not configured
   */
  public getWidgetSettingsOrDefault(attributeName: string, defaultValue: any): any {
    if (this.widgetConfig?.widgetSettings && this.widgetConfig?.widgetSettings[attributeName]) {
      return this.widgetConfig.widgetSettings[attributeName];
    }
    return defaultValue;
  }

  /**
   * Updates the widget settings in the report JSON
   * @param attributeName Attribute name
   * @param value Desired value
   */
  public setWidgetSettings(attributeName: string, value: any): void {
    if (this.widgetConfig) {
      if (!this.widgetConfig.widgetSettings) {
        this.widgetConfig.widgetSettings = {
          id: "0"
        };
      }
      this.widgetConfig.widgetSettings[attributeName] = value;
    }
  }

  /**
   * Prepares percentage data for displaying in UI (multiply by 100)
   * @param dataObject DTO
   * @param dataPropertyName Percentage value DTO property name
   */
  public percentageToUI(dataObject: any, dataPropertyName: string): void {
    const value = dataObject[dataPropertyName];
    dataObject[dataPropertyName] = value ? value * 100 : value;
  }

  /**
   * Prepares percentage data for sending to API (devide by 100)
   * @param dataObject DTO
   * @param dataPropertyName Percentage value DTO property name
   */
  public percentageFromUI(dataObject: any, dataPropertyName: string): void {
    const value = dataObject[dataPropertyName];
    dataObject[dataPropertyName] = value ? value / 100 : value;
  }

  /**
   * If missing, include zero entry to reference list data
   * @param reflistsData {any} Reference list data
   */
  public includeZeroEntryIfMissing(reflistsData): any[] {
    if (reflistsData && !reflistsData.find(item => item.oid === 0)) {
      const zeroEntry = { oid: 0, description: '' };
      reflistsData.unshift(zeroEntry);
    }

    return reflistsData;
  }

  /**
   * Gets the text & list settings from the report JSON (also sets defaults if not exists)
   * @param sectionName Section name
   */
  public getTextAndListSettingsOrSetDefault(sectionName: string, defaultValue: any): any {
    if (this.widgetConfig?.widgetSettings?.textAndListConfigs && this.widgetConfig?.widgetSettings?.textAndListConfigs[sectionName]) {
      return this.widgetConfig.widgetSettings.textAndListConfigs[sectionName];
    }

    //check if exists in old settings place ('textAndListConfig': without s at the end which is new placefor config (and also object that can hold multiple sections))
    if (this.widgetConfig?.widgetSettings?.textAndListConfig) {
      const currentValue = ObjectUtil.deepCopy(this.widgetConfig.widgetSettings.textAndListConfig);
      //move to right place
      this.setTextAndListSettings(sectionName, currentValue);
      //after moving settings, delete old, deprecated setting from report JSON
      delete this.widgetConfig.widgetSettings.textAndListConfig;
      //and return current value
      return currentValue;
    }

    //if not exists at all => set
    this.setTextAndListSettings(sectionName, defaultValue);

    // and return default values
    return defaultValue;
  }

  /**
   * Updates the text & list settings in the report JSON
   * New sttings schema is in form of object with multiple settings (per section), example:
   * textAndListConfigs: {
   * 'section1_name': settings for section 1,
   * 'section2_name': settings for section 2,
   * etc...
   * }
   * @param sectionName Section name
   * @param value Desired value
   */
  public setTextAndListSettings(sectionName: string, value: any): void {
    if (this.widgetConfig) {
      if (!this.widgetConfig.widgetSettings) {
        this.widgetConfig.widgetSettings = {
          id: "0"
        };
      }

      if (!this.widgetConfig.widgetSettings.textAndListConfigs) {
        this.widgetConfig.widgetSettings.textAndListConfigs = {};
      }

      this.widgetConfig.widgetSettings.textAndListConfigs[sectionName] = value;
      this.calculateSectionHasAvailableFields(sectionName);
    }
  }

  /**
   * Default handler for text&list section event sectionRowsCountChanged
   * @param sectionName Section name
   */
  public sectionRowsCountChanged(sectionName: string): void {
    this.calculateSectionHasAvailableFields(sectionName);
  }

  /**
   * Returns info if text&list section has some more available fields
   * @param sectionName Section name
   */
  public sectionHasAvailableFields(sectionName: string): boolean {
    const map = this._textAndListSectionHasFieldsAvailableMap;
    return !map.has(sectionName) ? true : map.get(sectionName);
  }

  /**
   * Calculates if there is any field available to add to section
   * @param sectionName Section name
   */
  private calculateSectionHasAvailableFields(sectionName: string): void {
    const config: any[] = this.widgetConfig.widgetSettings.textAndListConfigs[sectionName];
    const fieldsAvailable: boolean = config.find(item => !item.selected) !== undefined;
    this._textAndListSectionHasFieldsAvailableMap.set(sectionName, fieldsAvailable);
  }

  /**
   * Gets the current report
   * @returns
   */
  protected getReport(): any {
    return this.rdStorage.getReport();
  }

  /**
   * Gets a color from the color palette. Used by Kendo Charts
   * @param e
   */
  protected getColor(point): string {
    const paletteHelper = new ColorPalette();
    const items: any[] = point?.series?.data || [];
    const index = items.indexOf(point.dataItem);
    const length = items.length;
    return paletteHelper.getColor(length, index);
  }

  /**
   * Gets a color from the color palette. Used by Kendo Charts
   * @param index Index
   * @param length: Length
   */
  protected getColorByIndex(index: number, length: number): string {
    const paletteHelper = new ColorPalette();

    return paletteHelper.getColor(length, index);
  }

  /**
   * Can be overridden to get PIX Comps data
   */
  public getPixCompsData(): void {
    //Nothing. Can be overridden
  }

  /**
   * Gets the surrounding Gridster item element
   * @param element
   * @returns
   */
  protected getGridsterElement(element: ElementRef): any {
    return element.nativeElement.closest('.gridster-item');
  }
}
